/**
 * Trailing-debounce utilities (TDEV-266 Phase 5.2 plan.md:1148-1152).
 *
 * `createDebounced(fn, wait)` returns a callable that fires `fn` once `wait`
 * milliseconds have elapsed since the last call. Multiple rapid calls coalesce
 * to a single trailing fire with the most recent arguments.
 *
 * `createKeyedDebounced(fn, wait)` routes calls by their first argument (the
 * key) so concurrent bursts on independent keys do not coalesce. Phase 6
 * `SettingsSync` uses this variant to PATCH per top-level settings key without
 * cross-key interference.
 *
 * Both factories expose `.flush()` (force pending fire) and `.cancel()` (drop
 * pending fire). Implementation is intentionally tiny (~60 lines combined) and
 * uses `setTimeout` / `clearTimeout` so vitest's fake timers can drive it.
 */

type TimerHandle = ReturnType<typeof setTimeout>;

export interface Debounced<Args extends readonly unknown[]> {
  (...args: Args): void;
  flush(): void;
  cancel(): void;
}

export function createDebounced<Args extends readonly unknown[]>(
  fn: (...args: Args) => void,
  wait: number
): Debounced<Args> {
  let timer: TimerHandle | null = null;
  let pending: Args | null = null;

  const fire = () => {
    if (pending !== null) {
      const args = pending;
      pending = null;
      timer = null;
      fn(...args);
    }
  };

  const debounced = ((...args: Args) => {
    pending = args;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(fire, wait);
  }) as Debounced<Args>;

  debounced.flush = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    fire();
  };

  debounced.cancel = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    pending = null;
  };

  return debounced;
}

/**
 * Per-key trailing debounce. First positional argument is the routing key; the
 * remaining args are passed through to `fn`. Each key gets its own pending
 * timer + payload, so bursts on `'sidebar'` and `'theme'` fire independently.
 */
export interface KeyedDebounced<Rest extends readonly unknown[]> {
  (key: string, ...rest: Rest): void;
  flush(): void;
  cancel(): void;
}

export function createKeyedDebounced<Rest extends readonly unknown[]>(
  fn: (key: string, ...rest: Rest) => void,
  wait: number
): KeyedDebounced<Rest> {
  const timers = new Map<string, TimerHandle>();
  const pending = new Map<string, Rest>();

  const fireKey = (key: string) => {
    const args = pending.get(key);
    if (args !== undefined) {
      pending.delete(key);
      timers.delete(key);
      fn(key, ...args);
    }
  };

  const keyed = ((key: string, ...rest: Rest) => {
    pending.set(key, rest);
    const existing = timers.get(key);
    if (existing !== undefined) clearTimeout(existing);
    timers.set(
      key,
      setTimeout(() => fireKey(key), wait)
    );
  }) as KeyedDebounced<Rest>;

  keyed.flush = () => {
    const keys = Array.from(pending.keys());
    for (const key of keys) {
      const t = timers.get(key);
      if (t !== undefined) clearTimeout(t);
      fireKey(key);
    }
  };

  keyed.cancel = () => {
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
    pending.clear();
  };

  return keyed;
}
